/**
 * Mobile Drawer Navigation
 * Self-contained Web Component for mobile menu drawer
 * No dependencies on global.js
 */

class MobileDrawer extends HTMLElement {
  constructor() {
    super();

    // State
    this.isOpen = false;
    this.activeSubmenu = null;
    this.previousFocus = null;
    this.scrollPosition = 0;

    // Bound methods for event listeners
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleNavClick = this.handleNavClick.bind(this);
  }

  connectedCallback() {
    // Cache DOM references in connectedCallback (children are available here)
    this.toggleBtn = this.querySelector('.mobile-drawer__toggle');
    this.panel = this.querySelector('.mobile-drawer__panel');
    this.overlay = this.querySelector('.mobile-drawer__overlay');
    this.nav = this.querySelector('.mobile-drawer__nav');

    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    // Toggle button
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', this.handleToggleClick);
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Keyboard events
    document.addEventListener('keydown', this.handleKeyDown);

    // Event delegation for submenu triggers and back buttons
    if (this.nav) {
      this.nav.addEventListener('click', this.handleNavClick);
    }
  }

  unbindEvents() {
    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener('click', this.handleToggleClick);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }

    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.nav) {
      this.nav.removeEventListener('click', this.handleNavClick);
    }
  }

  /* ==========================================================================
     Public API
     ========================================================================== */

  /**
   * Open the drawer
   */
  open() {
    if (this.isOpen) return;

    // Close search drawer if open
    const searchDrawer = document.querySelector('search-drawer');
    if (searchDrawer && searchDrawer.isOpen) {
      searchDrawer.close();
    }

    this.isOpen = true;
    this.previousFocus = document.activeElement;
    this.scrollPosition = window.scrollY;

    // Add open class
    this.classList.add('mobile-drawer--open');

    // Lock body scroll
    document.body.classList.add('mobile-drawer-open');
    document.body.style.top = `-${this.scrollPosition}px`;

    // Update ARIA
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('aria-expanded', 'true');
    }

    // Trap focus
    this.trapFocus();

    // Dispatch event
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  /**
   * Close the drawer
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Close any open submenus first
    this.closeAllSubmenus();

    // Remove open class
    this.classList.remove('mobile-drawer--open');

    // Restore body scroll
    document.body.classList.remove('mobile-drawer-open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition);

    // Update ARIA
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('aria-expanded', 'false');
    }

    // Release focus
    this.releaseFocus();

    // Dispatch event
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  /**
   * Toggle drawer open/close
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /* ==========================================================================
     Submenu Navigation
     ========================================================================== */

  /**
   * Open a specific submenu
   * @param {HTMLElement} submenu - The submenu element to open
   */
  openSubmenu(submenu) {
    if (!submenu) return;

    submenu.classList.add('mobile-drawer__submenu--open');
    this.activeSubmenu = submenu;

    // Delay focus until after the CSS transition starts to prevent
    // the browser from scrolling to the off-screen element
    requestAnimationFrame(() => {
      const firstFocusable = submenu.querySelector(
        'button, a, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus({ preventScroll: true });
      }
    });
  }

  /**
   * Close the currently active submenu
   */
  closeSubmenu() {
    if (!this.activeSubmenu) return;

    const currentSubmenu = this.activeSubmenu;
    currentSubmenu.classList.remove('mobile-drawer__submenu--open');

    // Find parent submenu or main menu
    const parentSubmenu = currentSubmenu.closest(
      '.mobile-drawer__submenu--open'
    );
    this.activeSubmenu = parentSubmenu || null;

    // Focus the trigger that opened this submenu
    const trigger = this.querySelector(
      `[data-submenu-target="${currentSubmenu.id}"]`
    );
    if (trigger) {
      trigger.focus({ preventScroll: true });
    }
  }

  /**
   * Close all open submenus
   */
  closeAllSubmenus() {
    this.querySelectorAll('.mobile-drawer__submenu--open').forEach((submenu) => {
      submenu.classList.remove('mobile-drawer__submenu--open');
    });
    this.activeSubmenu = null;
  }

  /* ==========================================================================
     Event Handlers
     ========================================================================== */

  handleToggleClick(event) {
    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleOverlayClick(event) {
    event.preventDefault();
    this.close();
  }

  handleKeyDown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        if (this.activeSubmenu) {
          this.closeSubmenu();
        } else {
          this.close();
        }
        break;

      case 'Tab':
        // Let trapFocus handle this
        break;
    }
  }

  /**
   * Delegated click handler for submenu triggers and back buttons.
   * Using delegation avoids timing issues with direct binding.
   */
  handleNavClick(event) {
    // Check for submenu trigger
    const trigger = event.target.closest('[data-submenu-trigger]');
    if (trigger) {
      event.preventDefault();
      const targetId = trigger.getAttribute('data-submenu-target');
      const submenu = this.querySelector(`#${targetId}`);
      if (submenu) {
        this.openSubmenu(submenu);
      }
      return;
    }

    // Check for back button
    const back = event.target.closest('.mobile-drawer__back');
    if (back) {
      event.preventDefault();
      this.closeSubmenu();
      return;
    }
  }

  /* ==========================================================================
     Focus Management
     ========================================================================== */

  trapFocus() {
    if (!this.panel) return;

    const focusableElements = this.panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement.focus();

    // Handle tab key
    this.panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  releaseFocus() {
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
  }
}

// Register custom element
if (!customElements.get('mobile-drawer')) {
  customElements.define('mobile-drawer', MobileDrawer);
}
